import { inject, Injectable } from '@angular/core';
import { IItemsListSortOption } from '@fi-app/shared/fi-items-list/fi-items-list-sort-select/fi-items-list-sort-select.component';
import { IItemsListSort, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { AppTextService } from '@fi-shared/app-text';
import { DeepReadonly } from '@fi-app/shared/types/deep-readonly';
import { FiOnboardingCategoryType } from '../fi-onboarding-enums';
import { FiOnboardingStatusType } from '../fi-onboarding-enums';
import { FiOnboardingHelperService } from '../fi-onboarding-helper.service';

@Injectable()
export class FiOnboardingActiveHelperService {
    private readonly appTextService = inject(AppTextService);
    private readonly fiOnboardingHelperService = inject(FiOnboardingHelperService);

    private readonly _activationDateSortOption: IItemsListSortOption = {
        columnName: 'ActivationDate',
        textId: 'activation_date',
        defaultSortDirection: true,
        sortDirectionOptions: [
            {
                value: true,
                textId: 'sort.newest_first',
            },
            {
                value: false,
                textId: 'sort.oldest_first',
            },
        ],
    };

    private readonly _completedFractionSortOption: IItemsListSortOption = {
        columnName: 'CompletedFraction',
        textId: 'common.progress',
        defaultSortDirection: false,
        sortDirectionOptions: [
            {
                value: false,
                textId: 'sort.least_first',
            },
            {
                value: true,
                textId: 'sort.most_first',
            },
        ],
    };

    private readonly _completionDateSortOption: IItemsListSortOption = {
        columnName: 'CompletionEndDate',
        textId: 'task.completion_date',
        defaultSortDirection: true,
        sortDirectionOptions: [
            {
                value: true,
                textId: 'sort.newest_first',
            },
            {
                value: false,
                textId: 'sort.oldest_first',
            },
        ],
    };

    private readonly _inprogressSortOptions: IItemsListSortOption[] = [
        this._activationDateSortOption,
        this._completedFractionSortOption,
    ];
    private readonly _notStartedSortOptions: IItemsListSortOption[] = [this._activationDateSortOption];
    private readonly _completedSortOptions: IItemsListSortOption[] = [this._completionDateSortOption];
    private readonly _defaultSortOptions: IItemsListSortOption[] = [
        this._activationDateSortOption,
        this._completedFractionSortOption,
    ];

    getSortOptionsByStatus(status: FiOnboardingStatusType): IItemsListSortOption[] {
        switch (status) {
            case FiOnboardingStatusType.NotStarted:
                return this._notStartedSortOptions;
            case FiOnboardingStatusType.InProgress:
                return this._inprogressSortOptions;
            case FiOnboardingStatusType.Completed:
                return this._completedSortOptions;
            default:
                return this._defaultSortOptions;
        }
    }

    getSortByStatus(status: FiOnboardingStatusType): IItemsListSort {
        switch (status) {
            case FiOnboardingStatusType.NotStarted:
                return { ColumnName: 'ActivationDate', SortDirection: true };
            case FiOnboardingStatusType.InProgress:
                return { ColumnName: 'CompletedFraction', SortDirection: false };
            case FiOnboardingStatusType.Completed:
                return { ColumnName: 'CompletionEndDate', SortDirection: true };
            default:
                return { ColumnName: 'ActivationDate', SortDirection: true };
        }
    }

    readonly sideListOptions: DeepReadonly<ISideListOptionsItem[]> = [
        {
            value: undefined,
            text: this.appTextService.get('common.all'),
            counter$: this.fiOnboardingHelperService.allOnboardingsCount$,
        },
        {
            value: FiOnboardingStatusType.NotStarted,
            text: this.appTextService.get('onboarding.not_started'),
            counter$: this.fiOnboardingHelperService.notStartedOnboardingsCount$,
        },
        {
            value: FiOnboardingStatusType.InProgress,
            text: this.appTextService.get('task.in_progress'),
            counter$: this.fiOnboardingHelperService.inProgressOnboardingsCount$,
        },
        {
            value: FiOnboardingStatusType.Completed,
            text: this.appTextService.get('common.completed_plural'),
            counter$: this.fiOnboardingHelperService.completedOnboardingsCount$,
        },
    ];

    getOnboardingCardButtonLabelByStatus(status: FiOnboardingStatusType): string {
        switch (status) {
            case FiOnboardingStatusType.NotStarted:
                return this.appTextService.get('common.start');
            case FiOnboardingStatusType.InProgress:
                return this.appTextService.get('common.resume');
            case FiOnboardingStatusType.Completed:
                return this.appTextService.get('common.view_new');
        }
    }

    getOnboardingIconClassByType(type: FiOnboardingCategoryType): string {
        switch (type) {
            case FiOnboardingCategoryType.SyncReady:
                return 'far fa-merge';
            case FiOnboardingCategoryType.Independent:
                return 'far fa-swap-arrows';
            default:
                return 'far fa-swap-arrows';
        }
    }
}
